import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Layout from "@/components/Layout";
import StudentDashboard from "@/pages/StudentDashboard";
import ParentDashboard from "@/pages/ParentDashboard";
import Subject from "@/pages/Subject";
import Lesson from "@/pages/Lesson";
import Library from "@/pages/Library";
import Achievements from "@/pages/Achievements";
import Profile from "@/pages/Profile";
import ParentStudent from "@/pages/ParentStudent";
import WorksheetBuilder from "@/pages/WorksheetBuilder";
import WorksheetSolver from "@/pages/WorksheetSolver";
import ReportCard from "@/pages/ReportCard";
import EssayCoach from "@/pages/EssayCoach";
import Projects from "@/pages/Projects";
import Digest from "@/pages/Digest";
import { PassageList, PassageSolver } from "@/pages/Passages";
import Books from "@/pages/Books";
import BookReader from "@/pages/BookReader";
import Portfolio from "@/pages/Portfolio";
import Journal from "@/pages/Journal";
import Shelves from "@/pages/Shelves";
import FocusSprint from "@/pages/FocusSprint";
import ParentStudentView from "@/pages/ParentStudentView";
import Customize from "@/pages/Customize";
import Messages from "@/pages/Messages";
import HelpDesk from "@/pages/HelpDesk";
import Feedback from "@/pages/Feedback";
import AssignmentDetail from "@/pages/AssignmentDetail";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function HomeRouter() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!user) return <Landing />;
  return user.role === "parent" ? <Navigate to="/parent" replace /> : <Navigate to="/student" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomeRouter />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/student" element={<Protected role="student"><StudentDashboard /></Protected>} />
            <Route path="/parent" element={<Protected role="parent"><ParentDashboard /></Protected>} />
            <Route path="/parent/student/:studentId" element={<Protected role="parent"><ParentStudent /></Protected>} />
            <Route path="/parent/student/:studentId/view" element={<Protected role="parent"><ParentStudentView /></Protected>} />
            <Route path="/customize" element={<Protected role="student"><Customize /></Protected>} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/help" element={<HelpDesk />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/worksheets/new" element={<Protected role="parent"><WorksheetBuilder /></Protected>} />
            <Route path="/worksheets/:worksheetId" element={<WorksheetSolver />} />
            <Route path="/assignments/:assignmentId" element={<AssignmentDetail />} />
            <Route path="/report/:studentId" element={<ReportCard />} />
            <Route path="/essay" element={<Protected role="student"><EssayCoach /></Protected>} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/passages" element={<PassageList />} />
            <Route path="/passages/:passageId" element={<PassageSolver />} />
            <Route path="/books" element={<Books />} />
            <Route path="/books/:bookId" element={<BookReader />} />
            <Route path="/digest" element={<Protected role="parent"><Digest /></Protected>} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/journal" element={<Protected role="student"><Journal /></Protected>} />
            <Route path="/shelf" element={<Protected role="student"><Shelves /></Protected>} />
            <Route path="/focus" element={<Protected role="student"><FocusSprint /></Protected>} />
            <Route path="/subjects/:slug" element={<Subject />} />
            <Route path="/lessons/:lessonId" element={<Lesson />} />
            <Route path="/library" element={<Library />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/profile" element={<Profile />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
